import classnames from 'classnames';
import { head, pluck, propOr } from 'ramda';
import React, { FC, useCallback, useEffect, useMemo } from 'react';
import { useIntl } from 'react-intl';
import { Route, Switch, useHistory } from 'react-router';
import { CSSTransition, TransitionGroup } from 'react-transition-group';
import { noop } from 'rxjs';
import { DragLayer, Heading, NotificationContainer, Text } from '../../components/common';
import { AddFilesToBoardModal } from '../../components/common/BoardModals/AddFilesToBoardModal';
import { DuplexerEvent } from '../../constants/DuplexerEvent';
import { MediaType } from '../../constants/MediaType';
import { ModalName } from '../../constants/ModalName';
import { RoutePath } from '../../constants/Routes';
import { MainSource } from '../../constants/Source';
import { TranscodingStatus } from '../../constants/TranscodingStatus';
import { JumpTo } from '../../utils/hoc/withItemsReducer/withItemsReducer.types';
import { useFileType } from '../../utils/hooks/bi/useFileType';
import { useTrackActions } from '../../utils/hooks/bi/useTrackActions';
import { TrackDeleteBoardPopUpClickTarget, useTrackBoardAnalytics } from '../../utils/hooks/bi/useTrackBoardAnalytics';
import { useTrackImageEditToolClick } from '../../utils/hooks/bi/useTrackImageEditToolClick';
import { useTrackNextPageRender } from '../../utils/hooks/bi/useTrackNextPageRender';
import { ClickOrigin, useTrackTrashActions } from '../../utils/hooks/bi/useTrackTrashActions';
import { useTrackTrashButtons } from '../../utils/hooks/bi/useTrackTrashButtons';
import { useDuplexer } from '../../utils/hooks/useDuplexer';
import { useBoards } from '../../utils/hooks/useBoards';
import { useBoolean } from '../../utils/hooks/useBoolean';
import { useMediaManagerSDK } from '../../utils/hooks/useMediaManagerSDK';
import { useMediaStudioOnFileUploaded } from '../../utils/hooks/useMediaStudio';
import { useModal } from '../../utils/hooks/useModal';
import { useEditBoardModal } from '../../utils/hooks/useModal/modals/useEditBoardModal';
import {
  usePermanentDeleteModal,
  usePermanentDeleteModalHandlers,
} from '../../utils/hooks/useModal/modals/usePermanentDeleteModal';
import { Theme } from '../../utils/hooks/useModal/useModal.types';
import { useNotificatorConsumer } from '../../utils/hooks/useNotificator';
import { useScrollStream } from '../../utils/hooks/useScrollStream';
import { useSearchBIParams } from '../../utils/hooks/useSearchBIParams';
import { useSources } from '../../utils/hooks/useSources';
import { useVideoMakerOnFileUploaded } from '../../utils/hooks/useVideoMaker/useVideoMaker';
import { MediaManagerSDKAnyItemWithSectionInfo, MediaManagerSDKBoard } from '../../utils/media-manager-sdk';
import { getTranslatedItemName, isDividersCategory } from '../../utils/media-manager-sdk/items';
import { downloadItem } from '../../utils/privateMedia/download';
import { RouteBuilder } from '../../utils/RouteHelpers';
import { isCategoryOfNonVisualMediaType } from '../../utils/publicMedia';
import { ExpandedView } from '../ExpandedView';
import { Home } from '../Home';
import { ContextMenus } from './ContextMenus';
import styles from './FilesManager.scss';
import { IInner, IOuter } from './FilesManager.types';
import { useMoveToModal } from './hooks/useMoveToModal';
import { useTrashEmptyComplete } from './hooks/useTrashEmptyComplete';
import { deleteBoardNotification } from './notifications';
import { Panel } from './Panel';
import { RecommendedItems } from './RecommendedItems';
import { SearchResults } from './SearchResults';
import { SmartGrid } from './SmartGrid';
import { SourceContent } from './SourceContent';
import { TopBar } from './TopBar';

const defaultRecommendedItems = [];

export const FilesManagerComponent: FC<IOuter & IInner> = ({
  source,
  searchQuery,
  items,
  selectRecommendedCategories,
  selectedItems,
  editingItem,
  itemsFilters,
  className,
  sort,
  layout,
  settingsLayout,
  mediaTypes,
  publicMediaCurrentCategory,
  isLoading,
  hasMoreItems,
  areNextFilesLoading,
  isSecure,
  title,
  hasNoItems,
  isInExpandedMode,
  canItemsBeExpanded,
  resetFilesStream,
  isRecommended,
  isHome,
  isItemCut,
  isItemSelected,
  isItemEditing,
  clearItemsSelection,
  setSort,
  setLayout,
  setMediaType,
  clearAllExtendedFilters,
  restoreItems,
  handleItemClick,
  handleItemRightClick,
  handleEditItemStart,
  handleEditItemCancel,
  handleEditItemComplete,
  handleNameChange,
  openFolder,
  openFolderById,
  createNewFolderStartFromTop,
  currentFolder,
  folderPath,
  loadMoreItems,
  openExpandedView,
  closeExpandedView,
  handleEditExpandedItemCancel,
  updateLabels,
  searchByLabel,
  onUndoRemove,
  handleFileDoubleClick,
  handleDropOnFolder,
  selectAllMediaTypes,
  isActionBarVisible,
  isMediaTypeSelectVisible,
  isSortSelectVisible,
  isLayoutSwitchVisible,
  onUndoRemoveFromFavorites,
  onUndoAddToFavorites,
  buyItem,
  layouts,
  refreshFolder,
  footerContent,
  beginDrag,
  editSelectedImage,
  isSingleActionRowVisible,
  addItemToSelection,
  removeItemFromSelection,
  isMoreActionsVisible,
  isRestoreVisible,
  isDeleteForeverVisible,
  isEmptyTrashVisible,
  isCreateFolderVisible,
  selectItem,
  selectItems,
  prependItems,
  updateItem,
  handleSearchEnterPressed,
  handleSearchClearButtonClick,
  areNotAllMediaTypesSelected,
  demonstrationThumbnailStyle,
  handleJumpToNextItem,
  recommendedCategories,
  recommendedCategoriesError,
  isRecommendedCategoriesLoading,
  insertAfterFolders,
  updateFolderInPath,
  loadingError,
  emptyTrash,
  deleteForever,
  selectedItemsCount,
  handleFolderCreateInMoveToModal,
  moveSelectedItems,
  trackMediaManagerMediaPurchaseCompleted,
  updateLocalItem,
  removeItems,
  createNewFolderStart,
  hasCutItems,
  hasCopiedItems,
  pasteItems,
  copySelectedItems,
  cutSelectedItems,
  copyFileUrlToClipboard,
  moveItemsToTrash,
  startDownloadItem,
  folderId,
  onCopyItemEvent,
  onRestoreItemEvent,
  onEmptyTrashNotification,
  publicMediaRootCategories,
}) => {
  const recommendedItems = isHome ? items : defaultRecommendedItems;
  const history = useHistory();
  const { emitScroll } = useScrollStream();
  const { privateItemInfoMP, createZipMP, removeBoardsMP } = useMediaManagerSDK({ source, currentFolder });
  const { isExplorableMedia, isManageableMedia, isEnterprise } = useSources({ source, searchQuery });
  const searchBiParams = useSearchBIParams({ source, folderId });

  const { formatMessage } = useIntl();

  const handleFileUpload = useCallback(
    (file) => {
      if (currentFolder && currentFolder.id === file.folderId) {
        insertAfterFolders([file]);
        selectItem(file.id);
        setTimeout(() => emitScroll(file.id), 0);
      } else {
        history.push(RouteBuilder.Source(MainSource.SiteFiles, file.folderId, file.id));
      }
    },
    [currentFolder, insertAfterFolders, emitScroll, selectItem],
  );
  useMediaStudioOnFileUploaded(isHome ? noop : handleFileUpload);
  useVideoMakerOnFileUploaded(isHome ? noop : handleFileUpload);

  useTrashEmptyComplete(onEmptyTrashNotification);
  useDuplexer({
    [DuplexerEvent.CopyFolder]: onCopyItemEvent,
    [DuplexerEvent.CopyFile]: onCopyItemEvent,
    [DuplexerEvent.TrashFilesRestoreCompleted]: onRestoreItemEvent,
    [DuplexerEvent.TrashFoldersRestoreCompleted]: onRestoreItemEvent,
    [DuplexerEvent.OutputStatusUpdate]: ({ info }) => {
      if (!updateLocalItem) {
        return;
      }

      const { fileName, fileOutput, mediaType, opStatus } = info.data;
      updateLocalItem(fileName, { fileOutput });

      // for 3D files, SDK gets op_status=INPROGRESS for preview, but believes it is transcodingStatus,
      // so we need to reset it afterwards
      if (mediaType === MediaType.ThreeD && opStatus === TranscodingStatus.Ready) {
        updateLocalItem(fileName, { transcodingStatus: null });
      }
    },
    [DuplexerEvent.FileStatusUpdate]: ({ file }) => {
      if (!updateLocalItem) {
        return;
      }

      const { fileName, opStatus } = file;

      if (opStatus !== TranscodingStatus.Ready) {
        updateLocalItem(fileName, { transcodingStatus: opStatus, fileUrl: fileName });
        return;
      }

      privateItemInfoMP(fileName).then(({ data }) => {
        updateLocalItem(fileName, data);
      });
    },
  });

  useTrackNextPageRender({ isLoading, source, itemsFilters, items, areNextFilesLoading });
  const fileType = useFileType();

  const { trackImageEditToolClick } = useTrackImageEditToolClick();

  const moveToModal = useMoveToModal({ selectedItems, folderPath, moveSelectedItems, handleFolderCreateInMoveToModal });

  const { trackEmptyTrash, trackDeletedForever } = useTrackTrashActions();

  const permanentDeleteModal = usePermanentDeleteModal({
    selectedItemsCount,
    trackDeletedForever,
    selectedItems,
    deleteForever,
    trackEmptyTrash,
    emptyTrash,
  });

  const { trackDeleteBoardPopUpClick } = useTrackBoardAnalytics();

  const { notify } = useNotificatorConsumer();

  const { updateBoards, deleteItems } = useBoards();

  const editBoardModal = useEditBoardModal({
    source,
    prependItems,
    selectItem,
    updateLocalItem,
    currentFolder,
    updateFolderInPath,
  });

  const handleSelectItemsCallback = useCallback(
    ({ shouldUpdateItemsInFolder, items }) => {
      if (shouldUpdateItemsInFolder) {
        prependItems(items);
        selectItem(head(items).id);
      }
    },
    [prependItems, selectItem],
  );

  const addFilesToBoardModal = useModal(ModalName.AddFilesToBoard, {
    customLayout: true,
    noBodyPadding: true,
    width: '772px',
    title: formatMessage({ id: 'boards.modal-add-files-title' }),
    content: ({ additionalOptions: { boards } }) => (
      <AddFilesToBoardModal boards={boards} onSelectItems={handleSelectItemsCallback} />
    ),
    hideFooter: true,
  });

  const handleDeleteBoardClick = useCallback(
    async (boards: MediaManagerSDKBoard[]) => {
      await removeBoardsMP(pluck('id', boards));

      removeItems(boards);
      updateBoards();

      notify(deleteBoardNotification({ boards }));
    },
    [removeBoardsMP, removeItems],
  );

  const deleteBoardModal = useModal(ModalName.DeleteBoard, {
    theme: Theme.Destructive,
    width: '600px',
    image: <div className={styles.deleteBoardImage} />,
    title: ({ additionalOptions: { boards } }) => (
      <Heading size="medium">
        {boards && formatMessage({ id: 'boards.modal.delete.title' }, { boardsCount: boards.length })}
      </Heading>
    ),
    content: ({ additionalOptions: { boards } }) => (
      <Text>{boards && formatMessage({ id: 'boards.modal.delete.content' }, { boardsCount: boards.length })}</Text>
    ),
    cancelText: formatMessage({ id: 'boards.modal.delete.cancel' }),
    confirmText: formatMessage({ id: 'boards.modal.delete.confirm' }),
    onOk: ({ additionalOptions: { boards } }) => {
      trackDeleteBoardPopUpClick({
        boards,
        target: TrackDeleteBoardPopUpClickTarget.Delete,
      });

      handleDeleteBoardClick(boards);
    },
    onClose: ({ additionalOptions: { boards } }) =>
      trackDeleteBoardPopUpClick({
        boards,
        target: TrackDeleteBoardPopUpClickTarget.Close,
      }),
    onCancel: ({ additionalOptions: { boards } }) =>
      trackDeleteBoardPopUpClick({
        boards,
        target: TrackDeleteBoardPopUpClickTarget.Cancel,
      }),
  });

  const { trackEmptyTrashButtonClicked } = useTrackTrashButtons();
  const { trackActionsDownload } = useTrackActions();

  const { openModal: openRemoveFilesModal } = usePermanentDeleteModalHandlers();

  const handleEmptyTrashButtonClick = useCallback(() => {
    trackEmptyTrashButtonClicked();

    openRemoveFilesModal({ clickOrigin: ClickOrigin.TopBar });
  }, [trackEmptyTrashButtonClicked, openRemoveFilesModal]);

  const handleDownloadSelectedItems = useCallback(async () => {
    trackActionsDownload(selectedItems);
    const zipInfo = await createZipMP(selectedItems, { name: getTranslatedItemName(currentFolder, formatMessage) });
    const zipUrl = propOr<any, string>('', 'downloadUrl', zipInfo);

    if (zipUrl) {
      downloadItem(zipUrl);
      return;
    }
  }, [selectedItems, currentFolder, createZipMP, trackActionsDownload]);

  const handleRemoveItemsFromBoard = useCallback(
    async (items, biParams) => {
      await deleteItems(currentFolder as unknown as MediaManagerSDKBoard, items, biParams);
      removeItems(items);
    },
    [currentFolder, deleteItems, removeItems],
  );

  const handleRemoveSelectedItemsFromBoard = useCallback(
    (biParams) => handleRemoveItemsFromBoard(selectedItems, biParams),
    [selectedItems, handleRemoveItemsFromBoard],
  );

  const { value: isTopFilterVisible, set: setTopFilterVisibility } = useBoolean(false);

  useEffect(() => {
    if (isLoading) {
      setTopFilterVisibility(false);
    }
  }, [isLoading]);

  const messageProps = useMemo(
    () => ({ onUndoRemove, onUndoAddToFavorites, onUndoRemoveFromFavorites, openFolderById }),
    [onUndoRemove, onUndoAddToFavorites, onUndoRemoveFromFavorites, openFolderById],
  );

  const currentCategoryIsNonVisual = isCategoryOfNonVisualMediaType(publicMediaCurrentCategory);

  return (
    <div
      className={classnames(styles.filesManager, className, {
        [styles.withSingleActionRow]: isSingleActionRowVisible,
      })}
    >
      {!isHome && <NotificationContainer className={styles.notificationsContainer} messageProps={messageProps} />}

      <div className={styles.topBarAndContentAndPanel}>
        <div className={styles.topBarAndContent}>
          <TopBar
            source={source}
            restoreItems={restoreItems}
            emptyTrashButtonClicked={handleEmptyTrashButtonClick}
            isHome={isHome}
            isEnterpriseTab={isEnterprise}
            searchQuery={searchQuery}
            handleSearchEnterPressed={handleSearchEnterPressed}
            handleSearchClearButtonClick={handleSearchClearButtonClick}
            handleDropOnFolder={handleDropOnFolder}
            searchBiParams={searchBiParams}
            isLoading={isLoading}
            isMediaTypeSelectVisible={isMediaTypeSelectVisible}
            isSortSelectVisible={isSortSelectVisible}
            isLayoutSwitchVisible={isLayoutSwitchVisible}
            isActionBarVisible={isActionBarVisible}
            isTopFilterVisible={isTopFilterVisible}
            isEmptyTrashVisible={isEmptyTrashVisible}
            isCreateFolderVisible={isCreateFolderVisible}
            layouts={layouts}
            layout={layout}
            setLayout={setLayout}
            mediaTypes={mediaTypes}
            setMediaType={setMediaType}
            sort={sort}
            setSort={setSort}
            folderPath={folderPath}
            openFolder={openFolder}
            editSelectedImage={editSelectedImage}
            createNewFolderStartFromTop={createNewFolderStartFromTop}
            trackImageEditToolClick={trackImageEditToolClick}
            currentFolder={currentFolder}
          />

          <Switch>
            <Route path={RoutePath.Home}>
              <Home
                fileType={fileType}
                isLoading={isLoading}
                prependItems={prependItems}
                currentFolder={currentFolder}
                privateItemInfoMP={privateItemInfoMP}
                recommendedItems={recommendedItems}
                buyItem={buyItem}
                isSecure={isSecure}
                beginDrag={beginDrag}
                publicMediaCurrentCategory={publicMediaCurrentCategory}
                selectRecommendedCategories={selectRecommendedCategories}
                recommendedCategories={recommendedCategories}
                recommendedCategoriesError={recommendedCategoriesError}
                isRecommendedCategoriesLoading={isRecommendedCategoriesLoading}
                searchQuery={searchQuery}
                openFolderById={openFolderById}
                refreshFolder={refreshFolder}
                settingsLayout={settingsLayout}
                updateFolderInPath={updateFolderInPath}
                loadingError={loadingError}
                trackMediaManagerMediaPurchaseCompleted={trackMediaManagerMediaPurchaseCompleted}
                searchBiParams={searchBiParams}
                navigateTo={history.push}
              />
            </Route>

            <Route path={RoutePath.SearchAll}>
              <SearchResults
                refreshFolder={refreshFolder}
                searchQuery={searchQuery}
                isLoading={isLoading}
                items={items as MediaManagerSDKAnyItemWithSectionInfo[]}
                selectedItems={selectedItems}
                isItemSelected={isItemSelected}
                settingsLayout={settingsLayout}
                onItemClick={handleItemClick}
                onItemRightClick={handleItemRightClick}
                beginDrag={beginDrag}
                onDropOnFolder={handleDropOnFolder}
                onFileDoubleClick={handleFileDoubleClick}
                onFolderDoubleClick={openFolder}
                openExpandedView={openExpandedView}
                buyItem={buyItem}
                currentFolder={currentFolder}
              />
            </Route>
            <Route>
              {isRecommended ? (
                <RecommendedItems
                  items={items as MediaManagerSDKAnyItemWithSectionInfo[]}
                  isItemSelected={isItemSelected}
                  onItemClick={handleItemClick}
                  onItemRightClick={handleItemRightClick}
                  onFileDoubleClick={handleFileDoubleClick}
                  beginDrag={beginDrag}
                  openExpandedView={openExpandedView}
                  selectedItems={selectedItems}
                  buyItem={buyItem}
                  currentFolder={currentFolder}
                />
              ) : isExplorableMedia &&
                !isDividersCategory(publicMediaCurrentCategory) &&
                !currentCategoryIsNonVisual ? (
                <SmartGrid
                  beginDrag={beginDrag}
                  searchQuery={searchQuery}
                  refreshFolder={refreshFolder}
                  isTopFilterVisible={isTopFilterVisible}
                  setTopFilterVisibility={setTopFilterVisibility}
                  title={title}
                  items={items}
                  selectedItems={selectedItems}
                  editingItem={editingItem}
                  itemsFilters={itemsFilters}
                  source={source}
                  publicMediaCurrentCategory={publicMediaCurrentCategory}
                  currentFolder={currentFolder}
                  hasNoItems={hasNoItems}
                  hasMoreItems={hasMoreItems}
                  isLoading={isLoading}
                  isSecure={isSecure}
                  areNextFilesLoading={areNextFilesLoading}
                  canItemsBeExpanded={canItemsBeExpanded}
                  settingsLayout={settingsLayout}
                  resetFilesStream={resetFilesStream}
                  selectItem={selectItem}
                  selectItems={selectItems}
                  prependItems={prependItems}
                  clearItemsSelection={isEnterprise ? noop : clearItemsSelection} // causing a crash in enterprise tab
                  openExpandedView={openExpandedView}
                  updateLabels={updateLabels}
                  searchByLabel={searchByLabel}
                  loadMoreItems={loadMoreItems}
                  isItemEditing={isItemEditing}
                  isItemCut={isItemCut}
                  isItemSelected={isItemSelected}
                  onItemClick={handleItemClick}
                  addItemToSelection={addItemToSelection}
                  removeItemFromSelection={removeItemFromSelection}
                  onItemRightClick={handleItemRightClick}
                  onDropOnFolder={handleDropOnFolder}
                  onEditItemStart={handleEditItemStart}
                  onEditItemCancel={handleEditItemCancel}
                  onEditItemComplete={handleEditItemComplete}
                  onItemNameChange={handleNameChange}
                  onFileDoubleClick={handleFileDoubleClick}
                  onFolderDoubleClick={openFolder}
                  selectAllMediaTypes={selectAllMediaTypes}
                  mediaTypes={mediaTypes}
                  areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
                  buyItem={buyItem}
                  clearAllExtendedFilters={clearAllExtendedFilters}
                  updateItem={updateItem}
                  privateItemInfoMP={privateItemInfoMP}
                  publicMediaRootCategories={publicMediaRootCategories}
                />
              ) : (
                <SourceContent
                  beginDrag={beginDrag}
                  searchQuery={searchQuery}
                  refreshFolder={refreshFolder}
                  isTopFilterVisible={isTopFilterVisible}
                  setTopFilterVisibility={setTopFilterVisibility}
                  title={title}
                  items={items}
                  selectedItems={selectedItems}
                  editingItem={editingItem}
                  itemsFilters={itemsFilters}
                  source={source}
                  publicMediaCurrentCategory={publicMediaCurrentCategory}
                  currentFolder={currentFolder}
                  hasNoItems={hasNoItems}
                  hasMoreItems={hasMoreItems}
                  isLoading={isLoading}
                  isSecure={isSecure}
                  areNextFilesLoading={areNextFilesLoading}
                  layout={layout}
                  settingsLayout={settingsLayout}
                  resetFilesStream={resetFilesStream}
                  selectItem={selectItem}
                  selectItems={selectItems}
                  clearItemsSelection={isEnterprise ? noop : clearItemsSelection} // causing a crash in enterprise tab
                  openExpandedView={openExpandedView}
                  loadMoreItems={loadMoreItems}
                  isItemEditing={isItemEditing}
                  isItemCut={isItemCut}
                  isItemSelected={isItemSelected}
                  onItemClick={handleItemClick}
                  addItemToSelection={addItemToSelection}
                  removeItemFromSelection={removeItemFromSelection}
                  onItemRightClick={handleItemRightClick}
                  onDropOnFolder={handleDropOnFolder}
                  onEditItemStart={handleEditItemStart}
                  onEditItemCancel={handleEditItemCancel}
                  onEditItemComplete={handleEditItemComplete}
                  onItemNameChange={handleNameChange}
                  onFileDoubleClick={handleFileDoubleClick}
                  onFolderDoubleClick={openFolder}
                  selectAllMediaTypes={selectAllMediaTypes}
                  mediaTypes={mediaTypes}
                  areNotAllMediaTypesSelected={areNotAllMediaTypesSelected}
                  buyItem={buyItem}
                  clearAllExtendedFilters={clearAllExtendedFilters}
                  onRemoveItemsFromBoard={handleRemoveItemsFromBoard}
                />
              )}
            </Route>
          </Switch>
        </div>

        {isManageableMedia && currentFolder && (
          <Panel
            className={styles.panel}
            items={items}
            selectedItems={selectedItems}
            source={source}
            currentFolder={currentFolder}
            onFilesExpand={openExpandedView}
            canItemsBeExpanded={canItemsBeExpanded}
            onLabelsSave={updateLabels}
            onLabelClick={searchByLabel}
            updateFolderInPath={updateFolderInPath}
            updateItem={updateItem}
            selectItem={selectItem}
            prependItems={prependItems}
            privateItemInfoMP={privateItemInfoMP}
            downloadSelectedItems={handleDownloadSelectedItems}
            isMoreActionsVisible={isMoreActionsVisible}
            isRestoreVisible={isRestoreVisible}
            isDeleteForeverVisible={isDeleteForeverVisible}
            restoreItems={restoreItems}
            createNewFolderStartFromTop={createNewFolderStartFromTop}
            onRemoveItemsFromBoard={handleRemoveItemsFromBoard}
          />
        )}
      </div>

      {!isHome && (
        <>
          <div className={styles.footer}>{footerContent}</div>
          <TransitionGroup>
            {isInExpandedMode && (
              <CSSTransition classNames={styles} timeout={300}>
                <ExpandedView
                  className={styles.expandedView}
                  items={selectedItems}
                  editingItem={editingItem}
                  jumpToNextItem={handleJumpToNextItem({ jumpTo: JumpTo.Next })}
                  jumpToPrevItem={handleJumpToNextItem({ jumpTo: JumpTo.Prev })}
                  onClose={closeExpandedView}
                  onRenameCancel={handleEditExpandedItemCancel}
                  onNameChange={handleNameChange}
                  onRenameComplete={handleEditItemComplete}
                  footerContent={footerContent}
                  totalItemsCount={items.length}
                  demonstrationThumbnailStyle={demonstrationThumbnailStyle}
                />
              </CSSTransition>
            )}
          </TransitionGroup>

          <DragLayer />

          {moveToModal}
          {addFilesToBoardModal}
          {deleteBoardModal}
          {permanentDeleteModal}
        </>
      )}

      {!isHome && (
        <ContextMenus
          selectedItems={selectedItems}
          source={source}
          currentFolder={currentFolder}
          layout={layout}
          layouts={layouts}
          setLayout={setLayout}
          setSort={setSort}
          createNewFolderStart={createNewFolderStart}
          hasCutItems={hasCutItems}
          hasCopiedItems={hasCopiedItems}
          pasteItems={pasteItems}
          copySelectedItems={copySelectedItems}
          cutSelectedItems={cutSelectedItems}
          removeSelectedItemsFromBoard={handleRemoveSelectedItemsFromBoard}
          openExpandedView={openExpandedView}
          copyFileUrlToClipboard={copyFileUrlToClipboard}
          restoreItems={restoreItems}
          moveItemsToTrash={moveItemsToTrash}
          startDownloadItem={startDownloadItem}
          isInExpandedMode={isInExpandedMode}
          handleEditItemStart={handleEditItemStart}
          onDownloadSelectedItems={handleDownloadSelectedItems}
        />
      )}

      {editBoardModal}
    </div>
  );
};
